import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getFeaturedFilms } from "@/lib/catalog";

export default function HeroCarousel() {
  const featured = getFeaturedFilms();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || featured.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % featured.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [paused, featured.length]);

  const active = featured[index];
  if (!active) return null;

  const goTo = (nextIndex: number) => {
    setIndex((nextIndex + featured.length) % featured.length);
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点作品轮播"
    >
      <div className="absolute inset-0">
        {featured.map((film, filmIndex) => (
          <img
            key={film.id}
            src={film.heroImage || film.poster}
            alt={`${film.title} 焦点剧照`}
            className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-1000 ${
              filmIndex === index ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/85 to-primary-900/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-transparent to-primary-950/30" />
      </div>

      <div className="relative mx-auto flex max-w-shell flex-col gap-10 px-4 pb-10 pt-12 md:px-8 md:pb-14 md:pt-16 lg:min-h-[600px] lg:flex-row lg:items-end lg:justify-between lg:gap-14 lg:pb-16 lg:pt-24">
        <div className="w-full max-w-2xl">
          <p className="flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.28em] text-accent-300">
            <span className="h-[1px] w-8 bg-accent-400" />
            Japanese &amp; Korean Film Archive
          </p>
          <h1 className="mt-4 font-heading text-[38px] font-extrabold leading-[1.08] tracking-tight text-background-50 md:text-[56px] lg:text-[64px]">
            日韩伦理片
          </h1>
          <p className="mt-4 max-w-xl text-[14px] leading-relaxed text-primary-100/85 md:text-[15px]">
            以资料与解析为核心的内容站。收录日本与韩国的伦理题材电影、电视剧与短片，按主题、年代与地区整理，帮助你在最短时间内找到一个作品的来龙去脉。
          </p>

          <div className="mt-7 flex flex-col gap-2.5 sm:flex-row sm:items-center">
            <Link
              to="/category/%E7%94%B5%E5%BD%B1"
              className="flex cursor-pointer items-center justify-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              浏览全部电影
              <i className="ri-arrow-right-line text-sm" />
            </Link>
            <Link
              to="/specials"
              className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:border-background-50/70 hover:bg-background-50/10"
            >
              编辑策划专题
            </Link>
          </div>

          <div className="mt-9 flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              {featured.map((film, filmIndex) => (
                <button
                  key={film.id}
                  type="button"
                  aria-label={`切换到第 ${filmIndex + 1} 部作品`}
                  onClick={() => goTo(filmIndex)}
                  className={`h-1.5 cursor-pointer rounded-full transition-all ${
                    filmIndex === index ? "w-7 bg-accent-400" : "w-3 bg-background-50/40 hover:bg-background-50/70"
                  }`}
                />
              ))}
            </div>
            <span className="font-label text-[11px] tracking-[0.2em] text-primary-200/80">
              {String(index + 1).padStart(2, "0")} / {String(featured.length).padStart(2, "0")}
            </span>
            <div className="ml-1 flex items-center gap-1.5">
              <button
                type="button"
                aria-label="上一部焦点作品"
                onClick={() => goTo(index - 1)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-50/25 text-background-50/80 transition-colors hover:border-accent-300 hover:text-accent-300"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <button
                type="button"
                aria-label="下一部焦点作品"
                onClick={() => goTo(index + 1)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-50/25 text-background-50/80 transition-colors hover:border-accent-300 hover:text-accent-300"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>
        </div>

        <div className="w-full lg:max-w-[380px]">
          <div className="rounded-lg border border-background-50/20 bg-primary-950/45 p-4 backdrop-blur-sm md:p-5">
            <div className="flex items-center justify-between">
              <span className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] text-accent-300">
                本期焦点
              </span>
              <span className="rounded-full bg-background-50/10 px-2 py-0.5 font-label text-[10px] text-primary-100">
                {active.category} · {active.region}
              </span>
            </div>

            <div key={active.id} className="animate-soft-in mt-3.5 flex gap-4">
              <Link
                to={`/detail/${active.id}`}
                className="relative block h-[132px] w-[88px] shrink-0 cursor-pointer overflow-hidden rounded-md border border-background-50/20"
              >
                <img
                  src={active.poster}
                  alt={`${active.title} 作品封面`}
                  className="h-full w-full object-cover object-top"
                />
              </Link>
              <div className="flex min-w-0 flex-col">
                <Link
                  to={`/detail/${active.id}`}
                  className="cursor-pointer font-heading text-[20px] font-bold leading-tight text-background-50 transition-colors hover:text-accent-200"
                >
                  {active.title}
                </Link>
                <p className="mt-1 font-label text-[10px] uppercase tracking-wider text-primary-200/70">
                  {active.titleEn} · {active.year}
                </p>
                <p className="mt-2 line-clamp-3 text-[12px] leading-relaxed text-primary-100/80">
                  {active.tagline}
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-background-50/15 pt-3.5">
              <span className="font-label text-[11px] text-primary-200/80">
                评分 {active.score.toFixed(1)} · {active.duration}
              </span>
              <Link
                to={`/detail/${active.id}`}
                className="flex cursor-pointer items-center gap-1 font-label text-[11px] font-semibold text-accent-300 transition-colors hover:text-accent-200"
              >
                阅读解析
                <i className="ri-arrow-right-up-line text-xs" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}